import React, { useState } from 'react';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { FiX, FiCheck, FiCalendar, FiType, FiAlignLeft, FiLoader } from 'react-icons/fi';
import { toast } from 'sonner';

const MobileTaskModal = ({ isOpen, onClose }) => {
    const { activeWorkspace } = useWorkspace();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!activeWorkspace || !title.trim()) return;

        setLoading(true);
        const toastId = toast.loading("Creating task...");

        try {
            const { error } = await supabase
                .from('gnome_tasks')
                .insert({
                    workspace_id: activeWorkspace.id,
                    task_description: title,
                    // Assuming description goes to metadata or handled differently if schema allows
                    // For now, appending to title if description exists or just ignoring if schema is strict
                    due_at: dueDate ? new Date(dueDate).toISOString() : null,
                    status: 'pending',
                    priority: 'normal',
                    source: 'mobile_quick_action'
                });

            if (error) throw error;

            toast.success("Task created successfully!", { id: toastId });
            setTitle('');
            setDescription('');
            setDueDate('');
            onClose();
        } catch (err) {
            console.error("Task creation failed:", err);
            toast.error("Failed to create task.", { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4 animate-in fade-in duration-200">
            {/* Close Button */}
            <button
                onClick={onClose}
                className="max-h-[90vh] flex flex-col absolute top-4 right-4 text-white/50 hover:text-white p-2 rounded-full bg-white/10 backdrop-blur-md"
            >
                <FiX size={24} />
            </button>

            <div className="w-full max-w-sm bg-charcoal-900 border border-charcoal-700 rounded-2xl p-6 shadow-2xl">
                <h2 className="text-xl font-bold text-white mb-4">New Task</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-charcoal-400 mb-1">Task Title</label>
                        <div className="relative">
                            <FiType className="absolute left-3 top-3 text-charcoal-500" />
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-charcoal-800 border border-charcoal-700 rounded-lg text-white placeholder-charcoal-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                placeholder="What needs to be done?"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-charcoal-400 mb-1">Description (Optional)</label>
                        <div className="relative">
                            <FiAlignLeft className="absolute left-3 top-3 text-charcoal-500" />
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-charcoal-800 border border-charcoal-700 rounded-lg text-white placeholder-charcoal-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none h-24"
                                placeholder="Add details..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-charcoal-400 mb-1">Due Date (Optional)</label>
                        <div className="relative">
                            <FiCalendar className="absolute left-3 top-3 text-charcoal-500" />
                            <input
                                type="datetime-local"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-charcoal-800 border border-charcoal-700 rounded-lg text-white placeholder-charcoal-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none [color-scheme:dark]"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !title.trim()}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed mt-2 border border-blue-500"
                    >
                        {loading ? <FiLoader className="animate-spin" /> : <><FiCheck /> Create Task</>}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MobileTaskModal;
